import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import { uniq } from "lodash";

import { customRangeWithDayFormat } from "@similarweb/pro-logic-components";
import Sentry from "@similarweb/sentry-logger";

import { swSettings } from "app/common/services/swSettings";
import { adjustCreatedTime, campaignsFormatNb } from "app/components/campaigns/shared/CampaignsUtils";
import { translate } from "app/filters/i18nFilter";
import {
    CRUD_CAMPAIGN_URL,
    PERFORMANCE_ADD_ON_MUTUAL_ASSETS,
    SUBSCRIBE_CAMPAIGN_URL,
    UNSUBSCRIBE_CAMPAIGN_URL,
    CRUD_BRAND_MONITORING_CAMPAIGN_URL,
} from "app/pages/digital-marketing/brand-monitoring-campaigns/BrandMonitoringCampaignsConsts";
import type {
    IAssetsList,
    IBPCampaignDataFromServer,
    IBPCampaignDataFromServerAndAssets,
    IBPCurrentCampaignData,
    IBrandMonitoringCampaigns,
    IBrandMonitoringCampaignsEmailConfigurations,
    IBrandMonitoringCampaignsUpdateCampaignData,
} from "app/pages/digital-marketing/brand-monitoring-campaigns/wizard/BPCampaignsWizard.types";
import type {
    ICampaignsConsumptionMetrics,
    IMyUserConsumptionMetrics,
} from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignTypes";
import type { ISharedCampaign } from "app/pages/digital-marketing/keywords-campaigns/reports/keywords-report/keywords-table/KeywordsCamapaignKeywordsReportTableUtils";
import { UPDATE_SETTINGS_SUFFIX } from "app/common/campaigns/KeywordsCampaginKeywordsTableConsts";
import { DefaultFetchService } from "app/services/fetchService";
import { fetchWebsitesFaviconsQuery } from "app/services/sitesService/queries/sitesQueries";

import type { DatesRange } from "../reports/ads-report/shared/BrandMonitoringAdsReportUtils";

export const fetchService = DefaultFetchService.getInstance();

const getCampaignAsset = async (campaignConfigs: IBPCampaignDataFromServer[]): Promise<IAssetsList> => {
    const sitesList = campaignConfigs?.map((campaign) => {
        return [campaign.site];
    });
    const flatList = sitesList ? sitesList.flat() : [];
    const uniqSites = uniq(flatList);
    return uniqSites.length > 0 ? (await fetchWebsitesFaviconsQuery(uniqSites)) || {} : {};
};

export const fetchAddOnCampaignsMutualAssets = (): Promise<{
    CrossAccountExistingCampaigns: number;
    CrossAccountExistingKeywords: number;
}> => {
    return fetchService.get(PERFORMANCE_ADD_ON_MUTUAL_ASSETS, {}, { preventAutoCancellation: true }); //requires to prevent auto cancellation on navigation change during the fetch
};

export const fetchCampaigns = (): Promise<IBPCampaignDataFromServer[]> => {
    return fetchService
        .get(CRUD_CAMPAIGN_URL, {}, { preventAutoCancellation: true }) //requires to prevent auto cancellation on navigation change during the fetch
        .then((campaignConfigs: IBrandMonitoringCampaigns) => {
            const userCampaigns =
                campaignConfigs?.userCampaigns?.map((campaign) => ({
                    ...campaign,
                    createdTime: adjustCreatedTime(campaign.createdTime),
                    isShared: false,
                })) ?? [];
            const sharedCampaigns =
                campaignConfigs?.sharedCampaigns?.map((campaign) => ({
                    ...campaign,
                    createdTime: adjustCreatedTime(campaign.createdTime),
                    isShared: true,
                })) ?? [];
            return [...userCampaigns, ...sharedCampaigns];
        });
};

export const deleteCampaign = async (campaignId: string): Promise<IBPCampaignDataFromServer> => {
    return fetchService.delete(`${CRUD_CAMPAIGN_URL}/${campaignId}`);
};

export const updateCampaign = async (
    campaignData: IBrandMonitoringCampaignsUpdateCampaignData,
    campaignId: string,
): Promise<any> => {
    const update_url = `${CRUD_CAMPAIGN_URL}/${campaignId}/${UPDATE_SETTINGS_SUFFIX}`;
    return fetchService.put(update_url, campaignData);
};
export const subscribeToCampaign = async (
    campaignSubscriptionData: IBrandMonitoringCampaignsEmailConfigurations,
    campaignId: string,
): Promise<any> => {
    const update_url = `${SUBSCRIBE_CAMPAIGN_URL}/${campaignId}`;
    return fetchService.post(update_url, campaignSubscriptionData);
};
export const unsubscribeToCampaign = async (email: string, campaignId: string): Promise<any> => {
    const update_url = `${UNSUBSCRIBE_CAMPAIGN_URL}/${campaignId}?email=${email}`;
    return fetchService.delete(update_url);
};

export const wasDataScrapedForCampaign = (createdTime: Dayjs) => {
    const lastEtlDate = swSettings.components.BrandMonitor?.resources?.LastAvailableBrandMonitorEtlDate;
    return createdTime.startOf("day").isBefore(dayjs(lastEtlDate).startOf("day"));
};

export const getCampaignsAndKeywordsSubtitle = (
    campaignsConsumptionMetrics: ICampaignsConsumptionMetrics,
    myUserConsumptionMetrics: IMyUserConsumptionMetrics,
): string => {
    if (swSettings.user.isInternalUser) {
        return translate("brand.monitoring.campaigns.banner.balance.internalUser", {
            usedCampaigns: campaignsFormatNb(myUserConsumptionMetrics?.usedCampaigns),
            usedKeywords: campaignsFormatNb(myUserConsumptionMetrics?.usedKeywords),
            maxKeywords: swSettings.components.BrandMonitor.resources.MaxKeywordInternalBrandProtection,
        });
    }
    return translate("brand.monitoring.campaigns.banner.balance.subTitle", {
        usedCampaigns: campaignsFormatNb(campaignsConsumptionMetrics.usedCampaigns),
        totalCampaigns: campaignsFormatNb(campaignsConsumptionMetrics.totalCampaigns),
        usedKeywords: campaignsFormatNb(campaignsConsumptionMetrics.usedKeywords),
        totalKeywords: campaignsFormatNb(campaignsConsumptionMetrics.totalKeywords),
    });
};

const convertDurationObjToString = (durationObj: DatesRange) => {
    return `${durationObj.from.format(customRangeWithDayFormat)}-${durationObj.to.format(customRangeWithDayFormat)}`;
};

const isDateInRange = (date: Dayjs, lastEtlDate: Dayjs) => {
    const monthAgo = lastEtlDate.subtract(2, "month");
    return date.isBetween(monthAgo, lastEtlDate, null, "[]");
};

export const IsBrandMonitorSERPSnapshotButtonEnabled = (lastSeen: string) => {
    try {
        const lastEtlDate = dayjs(swSettings.components.BrandMonitor?.resources?.LastAvailableBrandMonitorEtlDate);
        const lastSeenDate = dayjs(lastSeen);
        return isDateInRange(lastSeenDate, lastEtlDate);
    } catch (error) {
        Sentry.error(`Error fetching table data BrandMonitor report - SERP snapshot`, error);
        return false;
    }
};

export const getBPCampaign = async (
    campaignId: string,
    campaignsConfigurations: ISharedCampaign<any>[],
): Promise<IBPCurrentCampaignData> => {
    const update_url = `${CRUD_BRAND_MONITORING_CAMPAIGN_URL}/${campaignId}`;
    const campaign: IBPCurrentCampaignData = await fetchService.get(update_url);
    const currentCampaignConfigShareState = campaignsConfigurations?.find(
        (campConf) => campConf.id === campaignId,
    )?.isShared;
    return {
        ...campaign,
        isShared: currentCampaignConfigShareState,
        createdTime: adjustCreatedTime(campaign.createdTime),
        updatedTime: adjustCreatedTime(campaign.updatedTime),
    };
};

export const getBPCampaignsFromServer = (): Promise<IBPCampaignDataFromServerAndAssets> => {
    return fetchCampaigns()
        .then((campaignConfigs: IBPCampaignDataFromServer[]) => {
            return getCampaignAsset(campaignConfigs).then(
                (assets: IAssetsList) => {
                    return {
                        campaignConfigs: campaignConfigs.map((config) => ({
                            ...config,
                        })),
                        assets,
                    };
                },
                (error) => {
                    Sentry.error(`Error fetching Brand monitoring listOfFavicons from server -`, error);
                    return {
                        campaignConfigs,
                        assets: {},
                    };
                },
            );
        })
        .catch((errorFetch) => {
            Sentry.error(`Error fetching Brand monitoring campaigns configurations from server -`, errorFetch);
            return null;
        });
};

export const createListOfCampaignsWithIcons = (
    listOfFavicons: IAssetsList,
    campaignConfigs: IBPCampaignDataFromServer[],
) => {
    const managedCampaigns: IBPCampaignDataFromServer[] = campaignConfigs.map((config) => {
        return {
            ...config,
            asset: listOfFavicons[config.site],
        };
    });
    const campaignsAsString = JSON.stringify(managedCampaigns);

    return {
        campaignsAsString,
        managedCampaigns,
    };
};
